"use client";

/**
 * The upload-flow paywall (Plan 02 Phase 3 / D3 wave 1): sak's storage and
 * file-cap blocking moments, rendered by the shared kit. The per-file cap
 * rides the entitlement snapshot's policy meter — never a hardcoded number —
 * and the server's own gates stay authoritative (this surface is
 * display-only; it skips files the plan cannot hold and opens the
 * `PaywallCard` modal at the blocking moment). The CTA round-trips into the
 * kit's `UpgradeSheet` when the catalog sells a pack for storage.
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BillingLabelsProvider,
  EN_DEFAULTS,
  PaywallCard,
  UpgradeSheet,
  formatBytes,
  usePaywall,
  type BillingLabels,
  type PaywallState,
  type Plan,
} from "@suite/billing";
import "@suite/billing/theme/billing.css";

import { sakBillingLabels } from "@/lib/billingLabels";

/** The upload paywall state + gate: which files may queue, when the modal
 *  opens, and the snapshot facts the kit surfaces render. Lives on the drive
 *  page because the gate wraps every upload entry point. */
export function useUploadPaywall(): {
  paywall: PaywallState;
  /** Split a picked batch: files the plan's per-file cap cannot hold are
   *  skipped (the modal opens); the rest queue untouched. An unknown cap
   *  (no snapshot) fails open — the server decides. */
  gateFiles: (files: File[]) => File[];
  modalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
} {
  const paywall = usePaywall("storage");
  const [modalOpen, setModalOpen] = useState(false);

  const gateFiles = useCallback(
    (files: File[]): File[] => {
      const cap = paywall.fileCapMeter?.limit ?? null;
      if (!cap || files.length === 0) return files;
      const allowed = files.filter((file) => file.size <= cap);
      if (allowed.length !== files.length) setModalOpen(true);
      return allowed;
    },
    [paywall.fileCapMeter],
  );

  return {
    paywall,
    gateFiles,
    modalOpen,
    openModal: () => setModalOpen(true),
    closeModal: () => setModalOpen(false),
  };
}

/**
 * The kit surfaces for the drive's paywall: the persistent over-limit banner
 * (only while the storage meter stands at or past its threshold), the
 * blocking-moment modal over the upload flow, and the checkout sheet both
 * CTAs open. One checkout state, one labels provider. Mount it in flow —
 * the banner lands where it is mounted; the modal and sheet are fixed
 * overlays whose DOM position does not matter.
 */
export function UploadPaywallSurfaces({ paywall, modalOpen, onCloseModal }: {
  paywall: PaywallState;
  modalOpen: boolean;
  onCloseModal: () => void;
}) {
  const { t } = useTranslation();
  const labels: BillingLabels = { ...EN_DEFAULTS, ...sakBillingLabels(t) };
  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);
  const cap = paywall.fileCapMeter?.limit ?? null;
  const openCheckout = paywall.pack ? (plan: Plan) => setCheckoutPlan(plan) : undefined;

  return (
    <BillingLabelsProvider labels={labels}>
      {paywall.blocked && (
        <PaywallCard
          service={labels.serviceStorage}
          variant="inline-banner"
          onUpgrade={openCheckout ? () => openCheckout(paywall.pack as Plan) : undefined}
        />
      )}
      {modalOpen && (
        <div
          className="suite-ui-dialog-backdrop"
          onMouseDown={(event) => event.target === event.currentTarget && onCloseModal()}
        >
          <div
            className="suite-ui-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={labels.paywallTitle(labels.serviceStorage)}
          >
            <div className="suite-ui-dialog-body">
              <PaywallCard
                service={labels.serviceStorage}
                variant="modal"
                body={cap !== null ? t("billing.fileCapBody", { size: formatBytes(cap) }) : undefined}
                onUpgrade={openCheckout ? () => openCheckout(paywall.pack as Plan) : undefined}
              />
            </div>
            <div className="suite-ui-dialog-footer">
              <span className="suite-billing-footer-spacer" />
              <button type="button" className="suite-billing-button" onClick={onCloseModal}>
                {labels.close}
              </button>
            </div>
          </div>
        </div>
      )}
      <UpgradeSheet
        open={checkoutPlan !== null}
        plan={checkoutPlan}
        onClose={() => setCheckoutPlan(null)}
        onSuccess={() => void paywall.refresh()}
      />
    </BillingLabelsProvider>
  );
}
